/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { BigModal } from './BigModal';
import { ModalButton } from './ModalButton';
import { Typography, Grid, Box } from '@mui/material';
import { SuccessModal, CustomTextField } from '../index';
import { apiUrl } from '../../utilities/apiUrl';
import { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useAlert } from '../../providers/AlertContext';

export const TwoFaModal = (props) => {
  const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false);
  const [otpFieldOpen, setOtpFieldOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const { showAlert } = useAlert();

  // Submission for activating 2FA
  const handleSubmit = async () => {
    const newIs2FAEnabled = !props.emp.is2faenabled;
    const resBody = {
      is2faenabled: !props.emp.is2faenabled, // toggle the flag
    };

    // Add OTP to the request body if deactivating 2FA
    if (props.emp.is2faenabled) {
      resBody.otp = otp;
    }

    fetch(apiUrl('/employee/' + props.emp._id + '/2fa'), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(resBody),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // update emp in order for frontend to render the the qr and key
          props.setEmp((prev) => ({
            ...prev,
            is2faenabled: newIs2FAEnabled,
            otpauthurl: data.data.otpauthurl,
            base32: data.data.base32,
          }));

          // Only show OTP field when ACTIVATING 2FA
          if (newIs2FAEnabled) {
            setOtpFieldOpen(true);
          } else {
            // Deactivating: Show success directly
            setUpdateSuccessOpen(true);
          }
        } else {
          showAlert(data.message, 'warning', 'Invalid Field');
        }
      })
      .catch((err) => {
        console.error('Error updating 2FA:', err);
      });
  };

  // Submission for verifying OTP after activating 2FA
  const handleVerify = async () => {
    const otpValue = otp.trim();
    if (!otpValue) {
      showAlert('Please enter your OTP.', 'warning', 'Invalid Input');
      return;
    }

    fetch(apiUrl('/auth/verify'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        otp: otpValue,
        empid: props.emp._id,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setUpdateSuccessOpen(true);
        } else {
          if (data.message == 'Error. Invalid OTP.') {
            showAlert('Please verify your OTP.', 'warning', 'Invalid OTP');
          }
        }
      });
  };

  const handleUpdateSuccessClose = () => {
    setUpdateSuccessOpen(false);
    setOtpFieldOpen(false);
    setOtp('');
  };

  return (
    <>
      <BigModal open={props.open} onClose={props.onClose}>
        <Typography sx={{ mb: 2, fontWeight: 600 }} variant="h5">
          Two-Factor Authentication
        </Typography>
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          ></Grid>
          {props.emp?.is2faenabled ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mx: 'auto',
                py: 4,
              }}
            >
              {/* Only show when users presses the activate button */}
              {otpFieldOpen ? (
                <>
                  <Typography variant="h8" sx={{ mb: 2, fontWeight: 600 }}>
                    Scan this QR Code in your Authenticator App
                  </Typography>

                  <QRCodeCanvas
                    value={props.emp?.otpauthurl}
                    size={180}
                    fgColor="#84d8c4"
                    bgColor="transparent"
                  />

                  <Typography variant="body2" sx={{ mt: 5 }}>
                    Or manually enter this key:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 500,
                      wordBreak: 'break-all',
                      color: 'text.secondary',
                    }}
                  >
                    {props.emp.base32}
                  </Typography>

                  <CustomTextField
                    error={!otp}
                    errorMsg={'Please enter OTP from Autheticator App'}
                    label={'Activation OTP'}
                    onChange={(event) => setOtp(event.target.value)}
                    onFocus={true}
                    required={true}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleVerify();
                      }
                    }}
                  />
                </>
              ) : (
                <>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Please enter OTP to remove Two-Factor Authentication.
                  </Typography>
                  <CustomTextField
                    error={!otp}
                    errorMsg={'Please enter OTP from Autheticator App'}
                    label={'OTP'}
                    onChange={(event) => setOtp(event.target.value)}
                    onFocus={true}
                    required={true}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleVerify();
                      }
                    }}
                  />
                </>
              )}
            </Box>
          ) : (
            <Grid
              item
              xs={12}
              sx={{ display: 'flex', justifyContent: 'center', py: 5 }}
            >
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Two-factor authentication is not activated yet.
              </Typography>
            </Grid>
          )}
        </Grid>
        <ModalButton
          primaryBtnName={
            otpFieldOpen
              ? 'Verify OTP'
              : props.emp?.is2faenabled
                ? 'Deactivate 2FA'
                : 'Activate 2FA'
          }
          secondaryBtnName="Cancel"
          primaryClickHandler={() => {
            if (otpFieldOpen) {
              handleVerify();
            } else {
              handleSubmit();
            }
          }}
          secondaryClickHandler={() => {
            props.onClose();
          }}
        />
      </BigModal>
      <SuccessModal
        open={updateSuccessOpen}
        onClose={() => {
          handleUpdateSuccessClose();
        }}
        close={handleUpdateSuccessClose}
        desc={`You have succesfully ${props.emp?.is2faenabled ? 'activated' : 'deactivated'} your 2FA.`}
      />
    </>
  );
};
